import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRole, getToken, getOrgToken, hasFeature, login, getVenueType, managerHomePath } from "../services/api";

// ----------------------------------------------------------------------
// LoginPage — staff sign-in only.
//
// Public self-registration is intentionally disabled. Hotels and
// restaurants engage with ODRA via the walkthrough form on the
// marketing site (odra.app/#walkthrough). Once a pilot or subscription
// agreement is signed, the founder creates the tenant's manager
// account in the admin panel and shares the credentials. This is the
// right model for a high-touch hospitality SaaS in pilot phase:
// quality leads, no fake accounts, controlled onboarding.
//
// The /signup route now redirects to the walkthrough form. Anyone who
// arrives at this LoginPage is either:
//   - An existing staff member signing in
//   - A returning user who got auto-redirected from / (already
//     handled by the useEffect below)
// ----------------------------------------------------------------------

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [needOtp, setNeedOtp] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // PWA launch flow:
  //   The home-screen icon (start_url = "/login" in manifest.json)
  //   lands here on every app open. Signed-out users see the sign-in
  //   form. Returning staff who have a cached JWT token are bounced
  //   immediately to "/" — the Home component there routes them to
  //   the right dashboard for their role. On a healthy session the
  //   user barely sees this page.
  useEffect(() => {
    if (getToken() && getRole()) {
      navigate("/", { replace: true });
    } else if (getOrgToken()) {
      // Owner (group) device: a hotel owner is signed in, not branch
      // staff. Bounce them straight to their owner dashboard so the PWA
      // opens where they expect instead of this staff sign-in screen.
      navigate("/owner", { replace: true });
    }
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Role removed: backend determines actual role from user's DB record.
      // Dropdown was purely cosmetic — friend-test feedback #76.
      const res = await login(email, password, needOtp ? otp : undefined);
      // MFA policy: a privileged account without 2FA must enroll before using
      // the dashboard — send them straight to the security page.
      if (res.mfa_enrollment_required) {
        navigate("/security", { replace: true });
        return;
      }
      // Land each role on its own home. Managers get a feature-aware shortcut
      // (free tier has no /reports); every other role goes to "/" so the Home
      // router picks the right landing (front desk, housekeeping, cashier…).
      // Sending non-managers straight to /kitchen bounced roles that aren't
      // allowed there into a redirect loop → white screen.
      let dest = "/";
      if (res.role === "manager") {
        dest = getVenueType() === "spa_gym" ? managerHomePath()
             : hasFeature("reports") ? "/reports" : "/menu";
      }
      navigate(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "2fa_required") {
        setNeedOtp(true);
        setError("Enter the 6-digit code from your authenticator app.");
      } else if (detail === "2fa_invalid") {
        setNeedOtp(true);
        setError("That code was not valid. Try again.");
      } else {
        setError(detail || "Sign-in failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        {/* ODRA logo on the sign-in card. Uses the horizontal wordmark
            (consistent with the dashboard navbar). */}
        <img
          src="/horizontal.svg?v=12"
          alt="ODRA"
          style={{
            display: "block",
            margin: "0 auto 18px",
            height: 90,
            maxWidth: "85%",
            objectFit: "contain",
          }}
          onError={(e) => {
            if (!e.currentTarget.dataset.fallback) {
              e.currentTarget.dataset.fallback = "vertical";
              e.currentTarget.src = "/vertical.svg?v=12";
              e.currentTarget.style.height = "140px";
            } else {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/logo192.png";
              e.currentTarget.style.height = "90px";
              e.currentTarget.style.borderRadius = "16px";
            }
          }}
        />
        <p style={{ textAlign: "center", margin: "0 0 18px", fontWeight: 600 }}>
          Staff sign-in
        </p>

        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourhotel.com"
            autoComplete="username"
            required
          />
        </div>

        <div className="field">
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {needOtp && (
          <div className="field">
            <label>Authentication code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              autoFocus
            />
          </div>
        )}

        {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

        <button className="btn" type="submit" disabled={busy} style={{ width: "100%" }}>
          {busy ? "Signing in…" : (needOtp ? "Verify & sign in" : "Sign in")}
        </button>

        <p style={{ textAlign: "center", margin: "14px 0 0", fontSize: "0.9rem" }}>
          <a
            href="/owner/login"
            onClick={(e) => { e.preventDefault(); navigate("/owner/login"); }}
            style={{ color: "var(--odra-teal)", fontWeight: 600, textDecoration: "none" }}
          >
            Hotel owner? Sign in to the owner dashboard →
          </a>
        </p>

        <hr style={{ margin: "20px 0 14px", border: 0, borderTop: "1px solid var(--border)" }} />

        {/* New visitor pathway — no self-registration on the staff app.
            Hotels engage via the walkthrough form on the marketing site;
            once an agreement is signed, the founder creates their
            account through the admin panel. */}
        <p className="muted" style={{ textAlign: "center", margin: 0, fontSize: "0.88rem", lineHeight: 1.5 }}>
          New to ODRA?{" "}
          <a
            href="https://odra.app/#walkthrough"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--odra-teal)", fontWeight: 600, textDecoration: "none" }}
          >
            Request a walkthrough →
          </a>
          <br />
          <span style={{ fontSize: "0.8rem" }}>
            Our team will set you up with on-site installation and training.
          </span>
        </p>
      </form>
    </div>
  );
}
